import type { IAnyWidgetParams } from '@core/types'
import { isFirefox } from '@core/userAgent'

import type { Theme } from './themes/types'

// Regex to remove prefixes from variable names.
const CLEAN_REGEX = new RegExp('(^--fw-|^--params-|^--theme-|^--)', 'ig')

/**
 * Clean up the variable name to remove prefixes.
 * @param name
 * @returns
 */
export function cleanVarName(name: string): string {
  return name.replace(CLEAN_REGEX, '')
}

/**
 * Transform UI params into `--params-` prefixed CSS variables they represent.
 * @param params
 * @returns
 */
export function cssVarsFromParams(params: IAnyWidgetParams): string {
  const {
    ui_font_css,
    ui_primary_color,
    ui_button_color,
    ui_button_font_color,
  } = params
  return `
    ${ui_font_css ? `@import url('${ui_font_css}');` : ''}    
    ${ui_primary_color ? `--params-button-color: ${ui_primary_color};` : ''}
    ${ui_primary_color ? `--params-primary: ${ui_primary_color};` : ''}
    ${
      ui_primary_color
        ? `--params-gradient: linear-gradient(${ui_primary_color}, ${ui_primary_color});`
        : ''
    }
    ${ui_button_color ? `--params-button-color: ${ui_button_color};` : ''}
    ${
      ui_button_font_color
        ? `--params-button-font-color: ${ui_button_font_color};`
        : ''
    }
  `
}

/**
 * Build a theme CSS declarations by transforming theme into `--theme-` prefixed CSS variables.
 * @param theme
 * @returns
 */
export function cssVarsFromTheme(theme: Theme): string {
  return `
    ${theme.variables
      .filter(([, value]) => !!value)
      .map(([name, value]) => `--theme-${cleanVarName(name)}: ${value};`)
      .join('\n')}

      ${
        theme.scheme?.light
          ? `@media (prefers-color-scheme: light) {
        ${theme.scheme.light
          .filter(([, value]) => !!value)
          .map(([name, value]) => `--theme-${cleanVarName(name)}: ${value};`)
          .join('\n')}
      }`
          : ''
      }

      ${
        theme.scheme?.dark
          ? `@media (prefers-color-scheme: dark) {
        ${theme.scheme.dark
          .filter(([, value]) => !!value)
          .map(([name, value]) => `--theme-${cleanVarName(name)}: ${value};`)
          .join('\n')}
      }`
          : ''
      }
  `
}

/**
 * Builds a CSS variable declaration with fallbacks.
 * Follows the precedence of
 *  1. `--fw-` prefix for Firework variables declared outside of the widget,
 *  2. `--params-` prefix for params variables received from channel settings, and
 *  3. `--theme-` prefix for theme variables.
 *  4. fallback
 *
 * Internal option means `--fw-` and `--params-` prefixes will be skipped.
 *
 * Resulting CSS declaration will look like this:
 * `--<name>: var(--fw-<name>, var(--params-<name>, var(--theme-<name>, <fallback>)));`
 */
export function cssVar(
  name: string,
  options: { external?: boolean; fallback?: string } = {},
): string {
  name = cleanVarName(name)
  const { fallback, external } = options

  function cssVarString(name: string, fallback?: string): string {
    return `var(${name}${fallback ? `, ${fallback}` : ''})`
  }

  const names: Array<string> = []
  if (external) {
    names.push(`--fw-${name}`)
    names.push(`--params-${name}`)
  }
  names.push(`--theme-${name}`)

  return `--${name}: ${names
    .reverse()
    .reduce((acc, name) => cssVarString(name, acc), fallback ? fallback : '')};`
}

export const SCROLL_BAR_STYLES = `
  // conditional because these properties override below pseudos in webkit
  // cascade importance is not enough
  ${
    isFirefox
      ? `
  scrollbar-width: thin; /* "auto" or "thin" */
  scrollbar-color: var(--scrollbar-thumb-color) var(--scrollbar-track-color);
  `
      : ''
  }
  
  &::-webkit-scrollbar {
    width: 4px;
    display: block !important;
    border:none;
    outline: none;
  }
  &::-webkit-scrollbar-track {
    background: var(--scrollbar-track-color);
    border: none;
    outline: none;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--scrollbar-thumb-color);
    border-radius: 2px !important;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: var(--primary);
  }
`
type UnitConverter = (value: number) => number
type UnitConverters = Record<string, UnitConverter>
/**
 * Convert absolute CSS numerical values to pixels
 *
 * @param {string} cssValue
 * @param {null|HTMLElement} target Used for relative units.
 * @return {*}
 */
export function cssUnitToPx(
  cssValue: string,
  target: HTMLElement = document.body,
): number | undefined {
  const supportedUnits: UnitConverters = {
    // Absolute sizes
    px: (value) => value,
    cm: (value) => value * 38,
    mm: (value) => value * 3.8,
    q: (value) => value * 0.95, // q is 1/40th of a cm
    in: (value) => value * 96,
    pc: (value) => value * 16,
    pt: (value) => value * 1.333333,

    // Relative sizes
    rem: (value) =>
      value *
      parseFloat(
        getComputedStyle(target.ownerDocument.documentElement).fontSize,
      ),
    em: (value) => value * parseFloat(getComputedStyle(target).fontSize),
    vw: (value) => (value / 100) * window.innerWidth,
    vh: (value) => (value / 100) * window.innerHeight,
    vmin: (value) =>
      (value / 100) * Math.min(window.innerWidth, window.innerHeight),
    vmax: (value) =>
      (value / 100) * Math.max(window.innerWidth, window.innerHeight),
  }

  // Match positive and negative numbers including decimals with following unit
  const pattern = new RegExp(
    `^([-+]?(?:\\d+(?:\\.\\d+)?))(${Object.keys(supportedUnits).join('|')})$`,
    'i',
  )

  // If is a match, return example: [ "-2.75rem", "-2.75", "rem" ]
  const matches = String.prototype.toString
    .apply(cssValue)
    .trim()
    .match(pattern)

  if (matches) {
    const value = Number(matches[1])
    const unit: string = matches[2].toLocaleLowerCase()

    // Sanity check, make sure unit conversion function exists
    if (unit in supportedUnits) {
      return supportedUnits[unit](value)
    }
  }

  return undefined
}
